import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { BuddyExportDataView } from '@adornis/bh-matching-export/db/BuddyExportDataView.js';
import type { TranslationDictionaryBuddy } from '@adornis/bh-matching-translation/translations.js';
import { BuddyUser } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { userFieldBlackList } from '@adornis/bh-matching-user/user-field-blacklist.js';
import type { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { exportDataView } from '@adornis/data-view/db/export-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { StringAspectConfig } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { buildBuddyExportBuildOptions } from '../api/BuddyExportAPI.js';

@customElement('buddy-dialog-prepare-export')
export class BuddyDialogPrepareExport extends TrDialog<void> {
  static override get element_name() {
    return 'buddy-dialog-prepare-export';
  }

  @property({ attribute: false }) dataOptions: Maybe<DataViewOptions<typeof BuddyUser>>;
  @property({ attribute: false }) timeframe: Maybe<Timeframe>;
  @property({ attribute: false }) userClass?: typeof BuddyUser;

  protected _exportAllFields = false;

  protected translator = new TranslationController<TranslationDictionaryBuddy>(this);

  protected async _export() {
    const timeframe = this.timeframe;
    if (!this.dataOptions || !this.userClass || !timeframe) return;

    const dataOptions: DataViewOptions<typeof BuddyUser> = { ...this.dataOptions, class: this.userClass };

    const fullOpt = this._exportAllFields
      ? buildBuddyExportBuildOptions(
          {
            ...dataOptions,
            customColumns: Object.values(this.userClass.fields)
              .filter(field => !userFieldBlackList.includes(field.name))
              .map(field => ({ fieldName: field.name })),
            view: new View({ ...dataOptions.view, configuredColumns: null }),
          },
          timeframe,
        )
      : buildBuddyExportBuildOptions({ ...dataOptions }, timeframe);

    if (!fullOpt.dataViewOptions.customColumns.find(customCol => customCol.fieldName === 'matrikelNummer'))
      fullOpt.dataViewOptions.customColumns.push({
        fieldName: 'matrikelNummer',
        renderItem: userEntity => {
          const matrikelNummer = (userEntity as { matrikelNummer?: string }).matrikelNummer;

          return matrikelNummer
            ? html` <x-text> ${this.translator.translate('enums', matrikelNummer)} </x-text> `
            : nothing;
        },
      });

    fullOpt.dataViewOptions.view?.filter?.aspects.push(
      new (StringAspectConfig('equals').aspectConstructor)({
        mode: 'equals',
        value: timeframe._id,
        fieldName: 'timeframeID',
      }),
    );
    await TrDialog.guard(() => exportDataView(fullOpt.dataViewOptions, { exportClass: BuddyExportDataView }))
      .then(() => XSnackbar.show(this._translation.translate('buddy', 'exportSuccess')))
      .catch(err => {
        console.error(err);
        TrDialog.alert(this._translation.translate('buddy', 'exportFailed'));
      });

    fullOpt.dataViewOptions.view?.filter?.aspects.pop(); // ToDo: Simple fix while same VarCursor from data-view is used

    return this.close();
  }

  override content() {
    return html`
      <tr-flex space="lg">
        <tr-flex space="md">
          <x-h4> ${this.translator.translate('route', 'export')}: </x-h4>

          <tr-checkbox
            label="${this._translation.translate('buddy', 'allFields')}"
            .value=${this._exportAllFields}
            @value-picked=${(e: ValueEvent<boolean>) => (this._exportAllFields = e.detail.value)}
          ></tr-checkbox>
        </tr-flex>

        <tr-flex crossaxis-center>
          <tr-button icon="upload" @click=${() => this._export()}>
            ${this.translator.translate('buddy', 'export')}
          </tr-button>
        </tr-flex>
      </tr-flex>
    `;
  }
}
